import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Button } from '@mui/material';

import { selectMaximumTravelDistance } from '../../../store/dataSelectors';
import {
    loadMaximumTravelDistanceDetails,
    loadParticipantStatusDetails,
    loadServiceDetailsByService
} from '../../../store/directusService';
import {
    clearContractConstantsSearch,
    loadParticipantsAdvancedSearch,
    searchContractConstants
} from '../../../store/participantService';
import { selectSortedTeamsForLoggedInUser } from '../../../store/userSelectors';
import {
    searchTeamsForLoggedInUser,
    searchUsersByLoggedInUserServiceIds
} from '../../../store/userService';
import { MAX_NAME } from '../../../utils/formValidation/commonConstants';
import { regExps } from '../../../utils/formValidation/validationPatterns';
import { addFullNameToArray } from '../../../utils/userArrayUtils';
import {
    ADVISER,
    hasRole,
    MANAGER,
    PRAP,
    QUALITY,
    RECRUITMENT_MANAGER,
    SUPERUSER
} from '../../../utils/userRoles';
import SingleSelect from '../../formElements/SingleSelect';
import TextInputField from '../../formElements/TextInputField';
import IconError from '../../IconError';
import LabelledTextField from '../../ui/editors/LabelledTextField';
import DDLOptionPicker from '../../ui/pickers/DDLOptionPicker';

import {
    EXTERNAL_REFERENCE_NUMBER_MAX_LENGTH,
    initialErrorState,
    validate
} from './participantSearchValidations';

import classes from '../../../app.module.css';
import form from '../../../commonStyles/formStyles.module.css';

const acceptedRoles = [ADVISER, MANAGER, PRAP, QUALITY, RECRUITMENT_MANAGER, SUPERUSER];

const ParticipantAdvancedSearch = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // LOCAL STATE

    const initialState = {
        serviceId: '',
        teamId: '',
        adviserId: '',
        statusId: '',
        blankPONumber: null,
        requestsAwaitingAuthorisation: null,
        postcode: '',
        maximumPostcodeDistanceId: '',
        notKnownAppointments: null,
        formsAwaitingAuthorisation: null,
        externalReferenceNumber: '',
        firstName: '',
        lastName: ''
    };

    const [newEntry, setNewEntry] = useState(initialState);
    const [teams, setTeams] = useState([]);
    const [filteredTeams, setFilteredTeams] = useState([]);
    const [filteredAdvisers, setFilteredAdvisers] = useState([]);
    const [errors, setErrors] = useState(initialErrorState);
    const [isDisabled, setIsDisabled] = useState(true);
    const [isExternalReferenceNumberEnabled, setIsExternalReferenceNumberEnabled] = useState(false);

    // STORE STATE
    const loggedInUser = useSelector((state) => state.entities.userService.loggedInUser);
    const { roles } = loggedInUser;
    const teamDetails = useSelector(selectSortedTeamsForLoggedInUser);
    const services = useSelector((state) => state.entities.directusService.serviceDetails);
    const users = useSelector((state) => state.entities.userService.usersByLoggedInUserServiceIds);
    const usersMetaData = useSelector(
        (state) => state.entities.userService.usersByLoggedInUserServiceIdsMetaData
    );
    const statuses = useSelector(
        (state) => state.entities.directusService.participantStatusDetails
    );
    const maximumTravelDistanceDetails = useSelector(selectMaximumTravelDistance);
    const contractConstantsSearch = useSelector(
        (state) => state.entities.participantService.contractConstantsSearch
    );

    // HELPER FNS

    const clearError = (key) => {
        setErrors((prev) => ({ ...prev, [key]: { ...errors[key], error: false, message: '' } }));
    };

    // USE EFFECTS

    useEffect(() => {
        if (!('serviceIds' in loggedInUser)) return;
        if (loggedInUser.serviceIds.length > 0) {
            dispatch(loadServiceDetailsByService(loggedInUser.serviceIds));
        }
        loggedInUser?.teamIds.length > 0 &&
            teamDetails?.length < 1 &&
            dispatch(searchTeamsForLoggedInUser({ ids: loggedInUser.teamIds }));
        dispatch(loadParticipantStatusDetails());
        dispatch(loadMaximumTravelDistanceDetails());
    }, [loggedInUser.id]);

    useEffect(() => {
        if (!loggedInUser.contractIds?.length) dispatch(clearContractConstantsSearch());
        else
            dispatch(
                searchContractConstants({
                    contractIds: loggedInUser.contractIds,
                    useExternalReferenceNumber: true
                })
            );
    }, [loggedInUser.contractIds]);

    useEffect(() => {
        setIsExternalReferenceNumberEnabled(!!contractConstantsSearch.length);
    }, [contractConstantsSearch]);

    useEffect(() => {
        if (!users.length) return;
        const updatedFilteredAdvisers = users.filter(
            (el) =>
                el.primaryService === newEntry.serviceId ||
                el.otherServices?.find((id) => id === newEntry.serviceId) ||
                el.primaryTeam?.id === newEntry.teamId ||
                el.otherTeams?.find((entry) => entry.id === newEntry.teamId)
        );
        if (!updatedFilteredAdvisers.length && !usersMetaData?.last) {
            onLoadMoreAdvisers();
            return;
        }
        setFilteredAdvisers([
            { id: 'isBlank', name: 'is blank' },
            ...addFullNameToArray(updatedFilteredAdvisers)
        ]);
    }, [users, newEntry.serviceId, newEntry.teamId]);

    useEffect(() => {
        if (teamDetails?.length < 1) return;
        setTeams(teamDetails);
        setFilteredTeams(teamDetails);
    }, [teamDetails]);

    useEffect(() => {
        setIsDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    // EVENT HANDLERS
    const onServiceChange = (chosenId) => {
        clearError('serviceId');
        setNewEntry((prev) => ({
            ...prev,
            serviceId: chosenId,
            teamId: '',
            adviserId: ''
        }));

        setFilteredTeams(!chosenId ? teams : teams.filter((el) => el.serviceId === chosenId));
    };

    const onTeamChange = (chosenId) => {
        setNewEntry((prev) => ({
            ...prev,
            teamId: chosenId,
            adviserId: ''
        }));
    };

    const onChangeMaximumPostcodeDistanceId = (chosenId) => {
        clearError('maximumPostcodeDistanceId');
        !chosenId && clearError('postcode');
        setNewEntry((prev) => ({
            ...prev,
            ...(!chosenId && { postcode: '' }),
            maximumPostcodeDistanceId: chosenId
        }));
    };

    const onSubmit = (e) => {
        e.preventDefault();
        const { isValid, newErrors } = validate(newEntry, errors);
        setErrors(newErrors);
        setIsDisabled(!isValid);
        if (!isValid) return;
        const {
            blankPONumber,
            requestsAwaitingAuthorisation,
            notKnownAppointments,
            formsAwaitingAuthorisation,
            adviserId,
            ...rest
        } = newEntry;
        const blankAdviser = adviserId === 'isBlank';
        const payload = {
            ...rest,
            blankPONumber: blankPONumber || null,
            requestsAwaitingAuthorisation: requestsAwaitingAuthorisation || null,
            notKnownAppointments: notKnownAppointments || null,
            formsAwaitingAuthorisation: formsAwaitingAuthorisation || null,
            blankAdviser,
            adviserId: blankAdviser ? '' : adviserId
        };
        dispatch(loadParticipantsAdvancedSearch(payload));
        navigate('/participant_search_results');
    };

    const onLoadMoreAdvisers = () => {
        if (usersMetaData?.last) return;
        const pageNumber = !users.length ? 0 : usersMetaData.number + 1 || 0;
        dispatch(searchUsersByLoggedInUserServiceIds(loggedInUser.serviceIds, pageNumber));
    };

    // RENDER

    return (
        <div className={classes.pageContainer}>
            <div className={classes.mainHeading}>Participant Advanced Search</div>
            <div className={classes.blueContainer}>
                <form className={form.formWrapper} onSubmit={onSubmit}>
                    <div className={form.formSection}>
                        <div className={form.formColumn}>
                            <DDLOptionPicker
                                label={'Service'}
                                id={'serviceId'}
                                mandatory={!newEntry.externalReferenceNumber}
                                menuItems={services || []}
                                chosenId={newEntry.serviceId}
                                onChange={(chosenId) =>
                                    onServiceChange(chosenId)
                                }></DDLOptionPicker>
                            {errors.serviceId.error && (
                                <div className={form.textInputErrors}>
                                    <IconError text={errors.serviceId} />
                                </div>
                            )}

                            <DDLOptionPicker
                                label={'Team'}
                                id={'teamId'}
                                menuItems={filteredTeams || []}
                                chosenId={newEntry.teamId}
                                onChange={(chosenId) => onTeamChange(chosenId)}></DDLOptionPicker>

                            <TextInputField
                                id={'firstName'}
                                label={'First Name'}
                                placeholder={'Enter first name...'}
                                maxLength={MAX_NAME}
                                value={newEntry.firstName || ''}
                                onChange={(e) =>
                                    setNewEntry((prev) => ({
                                        ...prev,
                                        firstName: e.target.value
                                    }))
                                }
                            />
                            <TextInputField
                                id={'lastName'}
                                label={'Last Name'}
                                placeholder={'Enter last name...'}
                                maxLength={MAX_NAME}
                                value={newEntry.lastName || ''}
                                onChange={(e) =>
                                    setNewEntry((prev) => ({
                                        ...prev,
                                        lastName: e.target.value
                                    }))
                                }
                            />

                            <SingleSelect
                                id={'adviserId'}
                                label={'Adviser'}
                                placeholder="Select adviser..."
                                disabled={!newEntry.serviceId}
                                menuItems={filteredAdvisers || []}
                                selectedId={newEntry.adviserId || ''}
                                selected={
                                    filteredAdvisers.find((el) => el.id === newEntry.adviserId) ||
                                    {}
                                }
                                onChange={(chosenId) =>
                                    setNewEntry((prev) => ({
                                        ...prev,
                                        adviserId: chosenId
                                    }))
                                }
                                onLoadMoreItems={onLoadMoreAdvisers}
                                moreItemsToLoad={
                                    !usersMetaData?.totalElements ||
                                    users.length < usersMetaData?.totalElements
                                }
                            />

                            <DDLOptionPicker
                                label={'Participant Status'}
                                id={'statusId'}
                                menuItems={statuses || []}
                                chosenId={newEntry.statusId}
                                onChange={(chosenId) =>
                                    setNewEntry((prev) => ({
                                        ...prev,
                                        statusId: chosenId
                                    }))
                                }></DDLOptionPicker>
                            <DDLOptionPicker
                                label={'Max Postcode Distance (Miles)'}
                                id={'maximumPostcodeDistanceId'}
                                menuItems={maximumTravelDistanceDetails || []}
                                chosenId={newEntry.maximumPostcodeDistanceId}
                                onChange={onChangeMaximumPostcodeDistanceId}></DDLOptionPicker>
                            {errors.maximumPostcodeDistanceId.error && (
                                <div className={form.textInputErrors}>
                                    <IconError text={errors.maximumPostcodeDistanceId} />
                                </div>
                            )}

                            <LabelledTextField
                                label={'Of Postcode'}
                                id={'postcode'}
                                value={newEntry.postcode}
                                placeholder={'Enter postcode'}
                                disabled={!newEntry.maximumPostcodeDistanceId}
                                onChange={(e) => {
                                    clearError('postcode');
                                    setNewEntry((prev) => ({
                                        ...prev,
                                        postcode: e.target.value.trim()
                                    }));
                                }}
                            />
                            {errors.postcode.error && (
                                <div className={form.textInputErrors}>
                                    <IconError text={errors.postcode} />
                                </div>
                            )}
                            {isExternalReferenceNumberEnabled && (
                                <TextInputField
                                    id={'externalReferenceNumber'}
                                    label={'External Reference Number'}
                                    placeholder={'Enter external reference number'}
                                    disabled={!hasRole(acceptedRoles, roles)}
                                    maxLength={EXTERNAL_REFERENCE_NUMBER_MAX_LENGTH}
                                    value={newEntry.externalReferenceNumber || ''}
                                    onChange={(e) =>
                                        setNewEntry((prev) => ({
                                            ...prev,
                                            externalReferenceNumber: e.target.value.replace(
                                                regExps.EXTERNAL_REFERENCE_NUMBER_R,
                                                ''
                                            )
                                        }))
                                    }
                                />
                            )}
                        </div>

                        <div className={form.formColumn}>
                            <div className={`${form.checkboxGroup} ${form.inputSet}`}>
                                <label
                                    htmlFor="requestsAwaitingAuthorisation"
                                    className={form.formLabel}>
                                    Requests Awaiting Authorisation
                                </label>
                                <input
                                    id="requestsAwaitingAuthorisation"
                                    type="checkbox"
                                    checked={newEntry.requestsAwaitingAuthorisation || false}
                                    onChange={(e) =>
                                        setNewEntry((prev) => ({
                                            ...prev,
                                            requestsAwaitingAuthorisation: e.target.checked
                                        }))
                                    }
                                />
                            </div>
                            <div className={`${form.checkboxGroup} ${form.inputSet}`}>
                                <label
                                    htmlFor="formsAwaitingAuthorisation"
                                    className={form.formLabel}>
                                    Forms Awaiting Authorisation
                                </label>
                                <input
                                    id="formsAwaitingAuthorisation"
                                    type="checkbox"
                                    checked={newEntry.formsAwaitingAuthorisation || false}
                                    onChange={(e) =>
                                        setNewEntry((prev) => ({
                                            ...prev,
                                            formsAwaitingAuthorisation: e.target.checked
                                        }))
                                    }
                                />
                            </div>
                            <div className={`${form.checkboxGroup} ${form.inputSet}`}>
                                <label htmlFor="blankPONumber" className={form.formLabel}>
                                    PO Number is Blank
                                </label>
                                <input
                                    id="blankPONumber"
                                    type="checkbox"
                                    checked={newEntry.blankPONumber || false}
                                    onChange={(e) =>
                                        setNewEntry((prev) => ({
                                            ...prev,
                                            blankPONumber: e.target.checked
                                        }))
                                    }
                                />
                            </div>
                            <div className={`${form.checkboxGroup} ${form.inputSet}`}>
                                <label htmlFor="notKnownAppointments" className={form.formLabel}>
                                    Previous Appointment Not Known
                                </label>
                                <input
                                    id="notKnownAppointments"
                                    type="checkbox"
                                    checked={newEntry.notKnownAppointments || false}
                                    onChange={(e) =>
                                        setNewEntry((prev) => ({
                                            ...prev,
                                            notKnownAppointments: e.target.checked
                                        }))
                                    }
                                />
                            </div>
                        </div>
                    </div>
                    <Button
                        type="submit"
                        color="primary"
                        variant="contained"
                        disabled={isDisabled}
                        onClick={onSubmit}>
                        Search
                    </Button>
                </form>
            </div>
        </div>
    );
};

export default ParticipantAdvancedSearch;
